import {
  DashboardEmployeeData,
  DatiDipendenteDaSalvare,
  Employee,
  EsitoSalvataggioDipendente,
  CellaCorretta,
  ImportChatReply,
  PrenotabiliPerCorso,
} from "../../types";
import {
  BE_BASE_URL,
  BE_EMPLOYEE_ENDPOINT,
  BE_IMPORT_CHAT_ENDPOINT,
  BE_IMPORT_EMPLOYEES_ENDPOINT,
  HTTP_TIMEOUT_CARICAMENTO_MS,
} from "../../utils/config";
import { format } from "date-fns";
import { axiosInstance } from "../../provider/authProvider";
import { goToPage } from "../../utils/utils";
import { toast } from "react-toastify";
import i18n from "i18next";

export function getEmployeeByCompanyId() {
  try {
    const axiosResponse = axiosInstance.get<Employee[]>(
      BE_BASE_URL + BE_EMPLOYEE_ENDPOINT + "byCompanyId"
    );
    return axiosResponse.then((response) => response.data);
  } catch {
    goToPage("500");
  }
}

export function getHiredEmployeeByCompanyId() {
  try {
    const axiosResponse = axiosInstance.get<Employee[]>(
      BE_BASE_URL + BE_EMPLOYEE_ENDPOINT + "byHiredCompanyId"
    );
    return axiosResponse.then((response) => response.data);
  } catch {
    goToPage("500");
  }
}

export function getEmployeeForReservationByRangeDate(
  courseId: string,
  dateSet: Date[]
) {
  try {
    const axiosResponse = axiosInstance.post<Employee[]>(
      BE_BASE_URL + BE_EMPLOYEE_ENDPOINT + "forReservationByRangeDate",
      {
        courseId: courseId,
        dateSet: dateSet,
      }
    );
    return axiosResponse.then((response) => response.data);
  } catch {
    goToPage("500");
  }
}

/*
 Le sedi di lavoro dell'azienda: il filtro sede delle tab attestati e i suggerimenti del campo
 "Sede di lavoro" in anagrafica. Se la chiamata fallisce chi ha chiamato resta usabile senza:
 toast e lista vuota, perche' la sede di lavoro e' testo libero e nessuna delle due schermate
 dipende da questo elenco per funzionare. Il messaggio e' un parametro perche' cambia il
 disagio a seconda di chi chiede: sparisce un filtro, oppure spariscono dei suggerimenti.
*/
export function getWorkplaces(
  chiaveErrore = "attestati-errore-sedi"
): Promise<string[]> {
  return axiosInstance
    .get<string[]>(BE_BASE_URL + BE_EMPLOYEE_ENDPOINT + "workplaces")
    .then((response) => response.data ?? [])
    .catch(() => {
      toast.error(i18n.t(chiaveErrore));
      return [];
    });
}

// I dipendenti attivi con sede di lavoro e mansione, per il riquadro
// Dipendenti della dashboard. Se fallisce il resto della pagina resta
// usabile: toast e riquadro vuoto.
export function getDashboardEmployees(): Promise<DashboardEmployeeData[]> {
  return axiosInstance
    // Senza slash finale: il backend risponde 400 a GET /employee/?dashboard=true.
    .get<DashboardEmployeeData[]>(BE_BASE_URL + BE_IMPORT_EMPLOYEES_ENDPOINT, {
      params: { dashboard: true },
    })
    .then((response) => response.data ?? [])
    .catch(() => {
      toast.error(i18n.t("dashboard-errore-dipendenti"));
      return [];
    });
}

// I dipendenti prenotabili di tutti i corsi in una sola lettura, per le
// tendine PRENOTA dei corsi mancanti (una chiave per codice corso). Se
// fallisce: toast e null, così chi chiama può ritentare più avanti.
export function getBookableEmployeesBatch(): Promise<PrenotabiliPerCorso | null> {
  return axiosInstance
    .get<PrenotabiliPerCorso>(BE_BASE_URL + BE_EMPLOYEE_ENDPOINT + "bookable")
    .then((response) => response.data ?? {})
    .catch(() => {
      toast.error(i18n.t("attestati-errore-prenotabili"));
      return null;
    });
}

export function formatDate(date: Date | undefined, pattern: string): string {
  return date ? format(date, pattern) : "";
}


/*
 Importazione guidata dei dipendenti.

 Consegna il foglio e apre la conversazione: il backend legge, non scrive, e risponde con
 l'esito riga per riga. Nessun dipendente entra in anagrafica finche' l'operatore non conferma
 dentro la conversazione.

 Il timeout e' quello lungo del caricamento: leggere un foglio di qualche centinaio di righe e
 confrontarlo con l'anagrafica non sta nei trenta secondi del client di default.
*/
export function avviaImportGuidato(file: File): Promise<ImportChatReply | null> {
  const formData = new FormData();
  formData.append("file", file);
  return axiosInstance
    .post<ImportChatReply>(BE_BASE_URL + BE_IMPORT_CHAT_ENDPOINT, formData, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: HTTP_TIMEOUT_CARICAMENTO_MS,
    })
    .then((response) => response.data)
    .catch((error) => {
      toast.error(
        error?.response?.data?.message ?? i18n.t("import-chat-errore-apertura")
      );
      return null;
    });
}

/*
 Un turno della conversazione. E' anche la strada da cui parte l'importazione vera, quando il
 messaggio dell'operatore e' una conferma: per questo il timeout e' quello lungo.
*/
export function inviaMessaggioImport(
  sessionId: string,
  text: string
): Promise<ImportChatReply | null> {
  return axiosInstance
    .post<ImportChatReply>(
      BE_BASE_URL + BE_IMPORT_CHAT_ENDPOINT + "/" + sessionId + "/message",
      { text },
      { timeout: HTTP_TIMEOUT_CARICAMENTO_MS }
    )
    .then((response) => response.data)
    .catch((error) => {
      toast.error(
        error?.response?.data?.message ?? i18n.t("import-chat-errore-messaggio")
      );
      return null;
    });
}

/*
 Correzioni fatte nella tabella dell'importazione guidata. Non passano dall'assistente e non
 toccano l'anagrafica: cambiano la bozza, e la risposta porta l'analisi rifatta. Se una cella non
 va (una data illeggibile) non se ne scrive nessuna, e il motivo arriva nel toast.
*/
export function correggiCelleImport(
  sessionId: string,
  cells: CellaCorretta[]
): Promise<ImportChatReply | null> {
  return axiosInstance
    .patch<ImportChatReply>(
      BE_BASE_URL + BE_IMPORT_CHAT_ENDPOINT + "/" + sessionId + "/rows",
      { cells }
    )
    .then((response) => response.data)
    .catch((error) => {
      toast.error(
        error?.response?.data?.message ?? i18n.t("import-chat-errore-correzione")
      );
      return null;
    });
}

/*
 Elimina un dipendente dall'anagrafica.

 La cancellazione lato backend e' logica: il dipendente sparisce da elenchi, dashboard,
 prenotabili e conteggi verso il limite dell'abbonamento, ma il record resta con i suoi
 attestati, e reinserirlo con lo stesso codice fiscale lo riprende com'era.

 La risposta e' 204 senza corpo. L'esito torna come booleano perche' chi chiama deve sapere se
 ricaricare la pagina: il messaggio di errore, quando c'e', lo mostra gia' il toast qui. Il
 403 di chi non ha il ruolo arriva a questa stessa catena, ed e' il motivo per cui il comando
 si mostra solo a chi puo' usarlo.
*/
export function eliminaDipendente(employeeId: number): Promise<boolean> {
  return axiosInstance
    .delete(BE_BASE_URL + BE_EMPLOYEE_ENDPOINT + employeeId)
    .then(() => true)
    .catch((error) => {
      toast.error(
        error?.response?.data?.message ?? i18n.t("employee-delete-errore")
      );
      return false;
    });
}

/*
 Inserisce o aggiorna un dipendente in anagrafica.

 Le due forme di esito sono separate perche' a schermo devono portare a due comportamenti
 diversi: un dipendente salvato chiude la modale, un salvataggio rifiutato la lascia aperta con
 i dati dentro, cosi' l'operatore corregge invece di riscrivere.

 Il caso che confonde e' quello di mezzo: la risposta puo' contenere insieme il dipendente
 salvato e un messaggio di rilievo — il codice fiscale che non supera la verifica non impedisce
 il salvataggio, per scelta. Li' l'esito e' `salvato`: il record c'e'. Il rilievo viaggia come
 `avviso` e chi chiama lo mostra a parte, senza spacciarlo per un errore.

 Il testo dell'errore arriva gia' scritto dal backend nel campo `message` e si mostra com'e':
 qui non si interpreta e non si ripulisce. Il toast lo fa chi chiama, non questa funzione,
 perche' l'esito guida anche la sorte della modale.
*/
export function salvaDipendente(
  dati: DatiDipendenteDaSalvare,
  nuovo: boolean
): Promise<EsitoSalvataggioDipendente> {
  // Senza slash finale: il backend risponde 400 a POST /employee/.
  const url = BE_BASE_URL + BE_EMPLOYEE_ENDPOINT.replace(/\/$/, "");
  const opzioni = { headers: { "Content-Type": "application/json" } };
  const chiamata = nuovo
    ? axiosInstance.post(url, { ...dati, id: undefined }, opzioni)
    : axiosInstance.put(url, dati, opzioni);

  return chiamata
    .then((response): EsitoSalvataggioDipendente => {
      const { employee, errorMessage, derivedStateMessage } =
        response.data ?? {};
      // Un 2xx senza dipendente nel corpo non e' un salvataggio: non va spacciato per riuscito.
      if (!employee) {
        return {
          esito: "rifiutato",
          messaggio: errorMessage || i18n.t("employee-save-errore-generico"),
        };
      }
      return {
        esito: "salvato",
        dipendente: employee,
        avviso: errorMessage || undefined,
        avvisoStato: derivedStateMessage || undefined,
      };
    })
    .catch((error): EsitoSalvataggioDipendente => ({
      esito: "rifiutato",
      messaggio:
        error?.response?.data?.message ||
        i18n.t("employee-save-errore-generico"),
    }));
}
